import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Search, Filter, ArrowLeft, Sparkles, Clock, TrendingUp, Heart, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PostCard } from '@/components/home/post-card'
import { EmptyState, PageTransition } from '@/components/ui/ux-enhancer'
import { useToast } from '@/components/ui/toast-provider'
import { useContentStore } from '@/stores/content-store'
import { SkeletonList } from '@/components/ui/skeleton-card'
import { apiAdapter } from '@/lib/api-adapter'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { POST_CATEGORIES } from '@/constants'

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [filters, setFilters] = useState({
    category: 'all',
    school: 'all',
    sortBy: 'relevance',
    timeRange: 'all'
  })

  const { posts, fetchPosts, isLoading } = useContentStore()
  const { addToast } = useToast()

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  // 时间格式化函数
  function formatTime(dateString: string): string {
    const now = new Date()
    const postTime = new Date(dateString)
    const diffInMinutes = Math.floor((now.getTime() - postTime.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return '刚刚'
    if (diffInMinutes < 60) return `${diffInMinutes}分钟前`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}小时前`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}天前`
    
    return postTime.toLocaleDateString('zh-CN')
  }

  // 执行搜索（调用后端 /posts/search）
  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    
    try {
      const response = await apiAdapter.posts.searchPosts(query, {
        page: 1,
        limit: 20,
        category: filters.category !== 'all' ? filters.category : undefined
      })

      if (response.success && response.data) {
        const results = (response.data.posts || []).map((p: any) => ({
          id: p._id,
          userId: p.author?._id || '',
          author: {
            name: p.isAnonymous ? '匿名用户' : (p.author?.nickname || p.author?.username || '未知用户'),
            avatar: p.isAnonymous ? '' : (p.author?.avatar || ''),
            school: '',
            level: 1
          },
          user: {
            name: p.isAnonymous ? '匿名用户' : (p.author?.nickname || p.author?.username || '未知用户'),
            avatar: p.isAnonymous ? '' : (p.author?.avatar || ''),
            school: '',
            level: 1
          },
          content: p.content || '',
          title: p.title || '',
          images: p.images || [],
          category: p.category || '其他',
          createdAt: new Date(p.createdAt),
          time: formatTime(p.createdAt),
          likes: p.likeCount || 0,
          comments: p.commentCount || 0,
          location: '',
          price: undefined,
          condition: undefined,
          tags: p.tags || []
        }))

        setSearchResults(results)
        
        if (results.length > 0) {
          addToast({
            type: 'success',
            title: '搜索完成',
            description: `找到 ${results.length} 条相关内容`
          })
        }
      } else {
        setSearchResults([])
      }
    } catch (error) {
      console.error('搜索失败:', error)
      addToast({
        type: 'error',
        title: '搜索失败',
        description: '请稍后重试'
      })
    } finally {
      setIsSearching(false)
    }
  }

  // 处理搜索提交
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery })
      performSearch(searchQuery)
    }
  }

  // 处理筛选变化
  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters)
    if (searchQuery.trim()) {
      performSearch(searchQuery)
    }
  }

  // 初始搜索（仅在 URL 参数变化时触发，避免依赖 posts 造成重复搜索）
  useEffect(() => {
    const query = searchParams.get('q')
    if (query) {
      setSearchQuery(query)
      performSearch(query)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  if (isLoading) {
    return (
      <PageTransition>
        <div className="space-y-6">
          {/* 搜索栏骨架屏 */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="w-full h-12 bg-gray-200 rounded-lg animate-pulse mb-4" />
            <div className="flex gap-2">
              <div className="w-20 h-8 bg-gray-200 rounded animate-pulse" />
              <div className="w-20 h-8 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
          
          <SkeletonList count={5} />
        </div>
      </PageTransition>
    )
  }

  return (
    <PageTransition>
      <div className="space-y-6">
        {/* 页面标题 */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            返回
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">搜索</h1>
              <p className="text-gray-600">发现你感兴趣的内容</p>
            </div>
          </div>
        </div>

        {/* 搜索栏 */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="搜索内容、标签或关键词..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-3 text-lg"
              />
              {searchQuery && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('')
                    setSearchResults([])
                    setSearchParams({})
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                >
                  清除
                </Button>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button type="submit" disabled={isSearching || !searchQuery.trim()}>
                {isSearching ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    搜索中...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    搜索
                  </>
                )}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const next = filters.category === 'all' ? '' : 'all'
                  setFilters(prev => ({ ...prev, category: next }))
                  if (searchQuery.trim()) {
                    performSearch(searchQuery)
                  }
                }}
              >
                <Filter className="w-4 h-4 mr-2" />
                筛选
              </Button>
              <div className="w-40">
                <Select
                  value={filters.category}
                  onValueChange={(value) => {
                    setFilters(prev => ({ ...prev, category: value }))
                    if (searchQuery.trim()) {
                      performSearch(searchQuery)
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部分类" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部分类</SelectItem>
                    {POST_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.icon} {cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </form>
        </div>

        {/* 搜索结果 */}
        <div className="space-y-4">
          {searchQuery && (
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                搜索结果 {searchResults.length > 0 && `(${searchResults.length})`}
              </h2>
              {searchResults.length > 0 && (
                <div className="text-sm text-gray-600">
                  为 "{searchQuery}" 找到 {searchResults.length} 条结果
                </div>
              )}
            </div>
          )}

          {isSearching ? (
            <SkeletonList count={3} />
          ) : searchQuery && searchResults.length === 0 ? (
            <EmptyState
              icon={<Search className="w-16 h-16 text-gray-300" />}
              title="未找到相关内容"
              description={`没有找到与 "${searchQuery}" 相关的内容，试试其他关键词吧`}
              action={
                <button
                  onClick={() => {
                    setSearchQuery('')
                    setSearchResults([])
                    setSearchParams({})
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  清除搜索
                </button>
              }
            />
          ) : searchResults.length > 0 ? (
            <div className="space-y-4">
              {searchResults.map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          ) : !searchQuery ? (
            <div className="bg-white rounded-lg p-8 shadow-sm text-center">
              <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">开始搜索</h3>
              <p className="text-gray-600 mb-6">输入关键词来搜索你感兴趣的内容</p>
              
              {/* 搜索建议 */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700">热门搜索</h4>
                <div className="flex flex-wrap gap-2 justify-center">
                  {['表白', '失物', '二手', '求助', '活动'].map(tag => (
                    <Button
                      key={tag}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSearchQuery(tag)
                        setSearchParams({ q: tag })
                        performSearch(tag)
                      }}
                    >
                      {tag}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </PageTransition>
  )
}